import React, { useEffect, useRef, useState } from 'react';
import { Animated, Pressable, StyleSheet, Text, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import Svg, {
  Defs,
  LinearGradient as SvgLinearGradient,
  Polygon,
  Stop,
} from 'react-native-svg';
import { doc, getDoc } from 'firebase/firestore';
import { FIRESTORE_COLLECTIONS, firestore } from '../firebase';
import { AURA_META, AuraType } from '../data/dayVibesQuestions';
import { Typography } from '../theme';
import { getDayVibeDateKey, getNextDayVibeBoundary, isDayVibesOpen } from './dayVibeDate';
import { useI18n } from '../i18n';

export type TodaysAura = {
  auraType: AuraType;
  auraName: string;
  icon: string;
  color: string;
  gradient: [string, string, string];
};

const RESPONSES = FIRESTORE_COLLECTIONS.dayVibeResponses;

export function getTodayAuraDateKey() {
  return getDayVibeDateKey();
}

export async function getTodaysAuraForUser(userId: string | null | undefined): Promise<TodaysAura | null> {
  const key = typeof userId === 'string' ? userId.trim() : '';
  if (!key || key === 'system') return null;
  if (!isDayVibesOpen()) return null;

  const dateKey = getTodayAuraDateKey();
  const responseId = `${key}_${dateKey}`;
  console.log('[Tonyte][DayVibes][AuraBadge] read today aura', {
    collectionPath: RESPONSES,
    documentId: responseId,
    authUid: key,
    dateKey,
  });
  const responseRef = doc(firestore, RESPONSES, responseId);
  const snap = await getDoc(responseRef);
  if (!snap.exists()) return null;

  const data = snap.data() as { auraType?: AuraType; auraName?: string; date?: string; dateKey?: string; submitted?: boolean };
  console.log('[Tonyte][DayVibes][AuraBadge] retrieved response data', {
    documentId: responseId,
    data,
  });
  if ((data.dateKey ?? data.date) !== dateKey || data.submitted === false) return null;
  if (!data.auraType || !AURA_META[data.auraType]) return null;

  const meta = AURA_META[data.auraType];
  return {
    auraType: data.auraType,
    auraName: data.auraName || meta.name,
    icon: meta.icon,
    color: meta.color,
    gradient: meta.gradient,
  };
}

export function useTodaysAura(userId: string | null | undefined) {
  return useTodaysAuraState(userId).aura;
}

// Same as useTodaysAura, plus whether today's lookup has finished — so a
// caller can tell "no answers yet today" (loaded, aura null) apart from
// "still loading".
export function useTodaysAuraState(userId: string | null | undefined) {
  const [aura, setAura] = useState<TodaysAura | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [clockKey, setClockKey] = useState(() => `${getTodayAuraDateKey()}:${isDayVibesOpen() ? 'open' : 'locked'}`);

  useEffect(() => {
    const now = new Date();
    const nextBoundary = getNextDayVibeBoundary(now);
    const delay = Math.max(1000, nextBoundary.getTime() - now.getTime() + 250);
    const timer = setTimeout(() => {
      setClockKey(`${getTodayAuraDateKey()}:${isDayVibesOpen() ? 'open' : 'locked'}`);
    }, delay);
    return () => clearTimeout(timer);
  }, [clockKey]);

  useEffect(() => {
    let cancelled = false;
    setAura(null);
    setLoaded(false);
    getTodaysAuraForUser(userId)
      .then(nextAura => {
        if (!cancelled) setAura(nextAura);
      })
      .catch(error => {
        console.warn('[Tonyte][RN][AuraBadge] failed to load today aura', { userId, error });
        if (!cancelled) setAura(null);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, clockKey]);

  return { aura, loaded };
}

export const TodaysAuraBadge: React.FC<{ aura: TodaysAura | null; compact?: boolean }> = ({ aura, compact = false }) => {
  if (!aura) return null;
  return (
    <View style={[styles.auraMarkWrap, compact && styles.auraMarkWrapCompact]}>
      <LinearGradient
        colors={aura.gradient}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={[styles.auraGlow, compact && styles.auraGlowCompact]}
      />
    </View>
  );
};

export const AuraProfileBadge: React.FC<{ aura: TodaysAura | null; compact?: boolean }> = ({ aura, compact = false }) => {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const shimmerAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    let active = true;
    const run = () => {
      if (!active) return;
      shimmerAnim.setValue(0);
      Animated.sequence([
        Animated.timing(shimmerAnim, { toValue: 1, duration: 1000, useNativeDriver: true }),
        Animated.delay(5200),
      ]).start(({ finished }) => { if (finished && active) run(); });
    };
    const timeoutId = setTimeout(run, 1400);
    return () => { active = false; clearTimeout(timeoutId); };
  }, [shimmerAnim]);

  if (!aura) return null;
  const width = compact ? 70 : 84;
  const height = compact ? 58 : 68;
  const auraName = aura.auraName.toUpperCase();
  const meta = AURA_META[aura.auraType];

  const shimmerX = shimmerAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [-width, width],
  });

  return (
    <Pressable
      style={[styles.profileBadgeWrap, compact && styles.profileBadgeWrapCompact, expanded && styles.profileBadgeWrapExpanded]}
      hitSlop={10}
      accessibilityRole="button"
      accessibilityLabel={t('vibes.auraBadgeAccessibility').replace('{name}', aura.auraName)}
      accessibilityHint={t('vibes.auraBadgeHint')}
      onPress={(event) => {
        event.stopPropagation?.();
        setExpanded(value => !value);
      }}
    >
      <View style={[styles.profileBadgeTilt, compact && styles.profileBadgeTiltCompact]}>
        {/* Aura bloom — barely-there tint, not a glow */}
        <View style={[styles.profileBadgeBloom, { backgroundColor: aura.color }]} />

        <Svg width={width} height={height} viewBox="0 0 84 68" style={styles.profileBadgeSvg}>
          <Defs>
            {/* Gunmetal/titanium fill — dominant dark metal, barely-there dark violet */}
            <SvgLinearGradient id="auraBadgeFill" x1="10" y1="4" x2="74" y2="64" gradientUnits="userSpaceOnUse">
              <Stop offset="0" stopColor="#CDD1DC" stopOpacity="0.96" />
              <Stop offset="0.2" stopColor="#565E72" stopOpacity="1" />
              <Stop offset="0.52" stopColor="#131620" stopOpacity="1" />
              <Stop offset="0.78" stopColor="#1C1828" stopOpacity="0.98" />
              <Stop offset="1" stopColor="#05060D" stopOpacity="1" />
            </SvgLinearGradient>
            {/* Chrome stroke — brushed titanium bevel edge */}
            <SvgLinearGradient id="auraBadgeStroke" x1="4" y1="6" x2="80" y2="62" gradientUnits="userSpaceOnUse">
              <Stop offset="0" stopColor="#E4E8F0" />
              <Stop offset="0.36" stopColor="#7A8498" />
              <Stop offset="0.66" stopColor="#5C6478" />
              <Stop offset="1" stopColor="#28303C" />
            </SvgLinearGradient>
            {/* Inner bevel — catch-light on raised lip */}
            <SvgLinearGradient id="auraBadgeBevel" x1="18" y1="14" x2="66" y2="54" gradientUnits="userSpaceOnUse">
              <Stop offset="0" stopColor="#FFFFFF" stopOpacity="0.28" />
              <Stop offset="0.42" stopColor="#9AAABB" stopOpacity="0.08" />
              <Stop offset="1" stopColor="#010204" stopOpacity="0.52" />
            </SvgLinearGradient>
          </Defs>
          {/* Outer frame — metallic hexagon */}
          <Polygon
            points="42,3 73,17 78,43 42,65 6,43 11,17"
            fill="url(#auraBadgeFill)"
            stroke="url(#auraBadgeStroke)"
            strokeWidth="2.4"
          />
          {/* Mid — dark matte enamel body */}
          <Polygon
            points="42,9 66,20 70,41 42,58 14,41 18,20"
            fill="rgba(3,5,14,0.74)"
            stroke="url(#auraBadgeBevel)"
            strokeWidth="1.2"
          />
          {/* Inner — deep enamel well with micro border */}
          <Polygon
            points="42,14 59,22 63,39 42,52 21,39 25,22"
            fill="rgba(6,8,18,0.84)"
            stroke="rgba(200,210,230,0.11)"
            strokeWidth="0.6"
          />
        </Svg>

        {/* Reflective shimmer sweep — clips to badge area at low opacity */}
        <Animated.View
          style={[styles.profileBadgeShimmer, { width, height, transform: [{ translateX: shimmerX }] }]}
          pointerEvents="none"
        >
          <LinearGradient
            colors={['transparent', 'rgba(255,255,255,0.05)', 'rgba(255,255,255,0.09)', 'rgba(255,255,255,0.05)', 'transparent']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={StyleSheet.absoluteFill}
          />
        </Animated.View>

        {/* Engraved badge content */}
        <View style={styles.profileBadgeContent}>
          <View style={[styles.profileBadgeIcon, compact && styles.profileBadgeIconCompact]}>
            <Icon name={aura.icon} size={compact ? 9 : 10} color="rgba(208,216,232,0.88)" />
          </View>
          <Text style={[styles.profileBadgeName, compact && styles.profileBadgeNameCompact]} numberOfLines={1}>
            {auraName}
          </Text>
        </View>
      </View>

      {/* Expanded description tooltip */}
      {expanded ? (
        <LinearGradient
          colors={['rgba(6,8,17,0.97)', 'rgba(12,10,22,0.95)', 'rgba(6,8,17,0.96)']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={[styles.profileBadgeDescription, compact && styles.profileBadgeDescriptionCompact]}
        >
          <Text style={styles.profileBadgeDescriptionTitle}>{aura.auraName}</Text>
          <Text style={styles.profileBadgeDescriptionText} numberOfLines={3}>
            {meta.description}
          </Text>
        </LinearGradient>
      ) : null}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  auraMarkWrap: {
    width: 112,
    height: 46,
    alignItems: 'center',
    justifyContent: 'center',
  },
  auraMarkWrapCompact: {
    width: 92,
    height: 38,
  },
  auraGlow: {
    width: '100%',
    height: '100%',
    borderRadius: 14,
    backgroundColor: '#6366F1',
    opacity: 0.58,
    shadowColor: '#fff',
    shadowOpacity: 0.22,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 0 },
    transform: [{ rotate: '-8deg' }],
  },
  auraGlowCompact: {
    borderRadius: 12,
  },
  profileBadgeWrap: {
    position: 'absolute',
    left: -20,
    top: -8,
    width: 84,
    height: 68,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#020617',
    shadowOpacity: 0.68,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 8 },
    elevation: 12,
    overflow: 'visible',
  },
  profileBadgeWrapCompact: {
    left: -17,
    top: -7,
    width: 70,
    height: 58,
  },
  profileBadgeWrapExpanded: {
    width: 192,
    height: 128,
    zIndex: 20,
  },
  profileBadgeTilt: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: 84,
    height: 68,
    alignItems: 'center',
    justifyContent: 'center',
    transform: [{ rotate: '-17deg' }],
  },
  profileBadgeTiltCompact: {
    width: 70,
    height: 58,
  },
  profileBadgeBloom: {
    position: 'absolute',
    width: 88,
    height: 62,
    borderRadius: 22,
    opacity: 0.05,
    transform: [{ rotate: '10deg' }],
  },
  profileBadgeSvg: {
    position: 'absolute',
  },
  profileBadgeShimmer: {
    position: 'absolute',
    top: 0,
    left: 0,
  },
  profileBadgeContent: {
    alignItems: 'center',
    justifyContent: 'center',
    width: '78%',
    gap: 3,
  },
  profileBadgeIcon: {
    width: 19,
    height: 19,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(4,6,14,0.88)',
    borderWidth: 1,
    borderColor: 'rgba(200,210,230,0.32)',
  },
  profileBadgeIconCompact: {
    width: 16,
    height: 16,
    borderRadius: 8,
  },
  profileBadgeName: {
    color: 'rgba(205,213,228,0.90)',
    fontSize: 8.5,
    lineHeight: 10,
    fontWeight: Typography.extrabold,
    maxWidth: 60,
    textAlign: 'center',
    letterSpacing: 1.1,
    textShadowColor: 'rgba(0,0,0,0.95)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  profileBadgeNameCompact: {
    fontSize: 7.5,
    lineHeight: 9,
    maxWidth: 48,
    letterSpacing: 0.9,
  },
  profileBadgeDescription: {
    position: 'absolute',
    left: 18,
    top: 60,
    width: 174,
    minHeight: 64,
    borderRadius: 16,
    backgroundColor: '#060811',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: 'rgba(150,162,190,0.22)',
    shadowColor: '#000',
    shadowOpacity: 0.52,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 6 },
  },
  profileBadgeDescriptionCompact: {
    left: 12,
    top: 52,
    width: 160,
    minHeight: 60,
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingVertical: 9,
  },
  profileBadgeDescriptionTitle: {
    color: 'rgba(218,224,238,0.96)',
    fontSize: 11,
    lineHeight: 14,
    fontWeight: Typography.extrabold,
    letterSpacing: 1.0,
    textTransform: 'uppercase',
    marginBottom: 3,
  },
  profileBadgeDescriptionText: {
    color: 'rgba(190,198,218,0.82)',
    fontSize: 10.5,
    lineHeight: 14,
    fontWeight: Typography.semibold,
  },
});
